import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  fetchPendingMediaPlans,
  fetchMediaPlanValidation,
  approveMediaPlan,
  type MediaPlanSummary,
  type ValidationResponse,
} from '@/pages/media-plan-approval/api';
import { getPermissions } from '@/api/users';
import { useCurrentUser } from '@/hooks/useUserRole';
import { getDefaultMarketFilter } from '@/utils/defaultMarketFilter';
import { getDefaultChannelFilter } from '@/utils/defaultChannelFilter';
import { ConfirmModal } from '@/components/ConfirmModal';
import { FlagIcon } from '@/components/FlagIcon';
import { DcoBadge } from '@/components/DcoBadge';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PAGE_PADDING, tableScrollMaxHeight } from '@/components/layout/layoutConstants';
import { useHeaderHeight } from '@/components/layout/useHeaderHeight';
import {
  XCircle,
  AlertTriangle,
  CheckCircle,
  ChevronUp,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';
import { ChannelIcon } from '@/pages/media-plan-approval/components/ChannelIcon';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { MediaPlanHeader } from '@/pages/media-plan-approval/components/Header';
import { GroupedIssues } from '@/pages/media-plan-approval/components/GroupedIssues';
import { MediaPlanTableSkeleton } from '@/pages/media-plan-approval/components/MediaPlanTableSkeleton';
import { MapSyncFooter } from '@/components/MapSyncFooter';
import { isFeatureEnabled } from '@/config/featureFlags';

type SortField =
  | 'plan_id'
  | 'campaign_count'
  | 'channel'
  | 'market'
  | 'business_unit'
  | 'year'
  | 'timeline'
  | 'status';

const SortIcon = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: 'asc' | 'desc';
}) => {
  if (sortField !== field) {
    return (
      <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
        />
      </svg>
    );
  }
  return sortDirection === 'asc' ? (
    <svg className="w-4 h-4 text-[#0014cc]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-[#0014cc]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
};

export const MediaPlanApprovalPage = () => {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const [approvingPlanId, setApprovingPlanId] = useState<string | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<MediaPlanSummary | null>(null);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);

  // Deep link support: ?plan=X to scroll to and highlight a plan row
  const [highlightedPlanId, setHighlightedPlanId] = useState<string | null>(null);
  const hasProcessedDeepLink = useRef(false);

  // Measured rather than hardcoded: the header grows when filters wrap or a
  // badge row is added, and a fixed value would push the table off-screen.
  const { headerRef, headerHeight } = useHeaderHeight();

  const deepLinkPlanId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('plan') || null;
  }, []);

  // Filter state
  const defaultFilters = {
    channel: getDefaultChannelFilter(currentUser?.channel),
    market: getDefaultMarketFilter(currentUser?.market),
    business_unit: '',
    status: '',
  };
  const [filters, setFilters] = useState<typeof defaultFilters>(() => {
    try {
      const saved = sessionStorage.getItem('pa-filters');
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return defaultFilters;
  });

  useEffect(() => {
    sessionStorage.setItem('pa-filters', JSON.stringify(filters));
  }, [filters]);
  // Apply user's default channel/market filters when currentUser first loads
  const hasAppliedUserDefaults = useRef(false);
  useEffect(() => {
    if (!currentUser || hasAppliedUserDefaults.current) return;
    hasAppliedUserDefaults.current = true;
    setFilters((prev) => ({
      ...prev,
      channel: prev.channel || getDefaultChannelFilter(currentUser.channel),
      market: prev.market || getDefaultMarketFilter(currentUser.market),
    }));
  }, [currentUser]);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('plan_id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Fetch user permissions
  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    isError: isPermissionsError,
  } = useQuery({
    queryKey: ['user-permissions'],
    queryFn: getPermissions,
    staleTime: 5 * 60 * 1000,
  });

  const canApprove =
    !isLoadingPermissions && !isPermissionsError && (permissions?.can_approve_media_plan ?? false);

  const dcoApprovalEnabled = isFeatureEnabled('dcoApproval');

  // Fetch pending media plans
  const { data, isLoading, error } = useQuery({
    queryKey: ['pending-media-plans'],
    queryFn: fetchPendingMediaPlans,
    staleTime: 30 * 1000, // 30s — prevents immediate refetch on mount/focus from clearing validation cache
    refetchInterval: (query) => {
      const hasPendingPlans = (query.state.data?.total_count ?? 0) > 0;
      return hasPendingPlans ? 60000 : false;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    placeholderData: (prev) => prev, // Keep previous data during refetch to avoid validation query reset
  });

  // Fetch validation for all pending media plans in background
  // Query key includes last_import_at so validation re-runs after each MAP import
  // Results are cached in sessionStorage so they survive page refresh without showing "Validating..."
  const validationQueries = useQueries({
    queries: (data?.media_plans ?? []).map((plan) => ({
      queryKey: ['media-plan-validation', plan.plan_id, data?.last_import_at],
      queryFn: async () => {
        const result = await fetchMediaPlanValidation(plan.plan_id);
        // Persist to sessionStorage for instant display on refresh
        try {
          sessionStorage.setItem(
            `ct-validation-${plan.plan_id}`,
            JSON.stringify({ data: result, importAt: data?.last_import_at, fetchedAt: Date.now() }),
          );
        } catch {
          /* storage full — ignore */
        }
        return result;
      },
      staleTime: 60 * 60 * 1000, // 1 hour — imports trigger re-validation via query key change
      retry: 1,
      initialData: (): ValidationResponse | undefined => {
        try {
          const cached = sessionStorage.getItem(`ct-validation-${plan.plan_id}`);
          if (!cached) return undefined;
          const parsed = JSON.parse(cached);
          // Only use cache if it matches the current import
          if (parsed.importAt === data?.last_import_at) return parsed.data;
        } catch {
          /* ignore */
        }
        return undefined;
      },
      initialDataUpdatedAt: () => {
        // Return the actual fetch timestamp so React Query knows when the cached data was last validated
        try {
          const cached = sessionStorage.getItem(`ct-validation-${plan.plan_id}`);
          if (!cached) return 0;
          const parsed = JSON.parse(cached);
          if (parsed.importAt === data?.last_import_at) return parsed.fetchedAt ?? 0;
        } catch {
          /* ignore */
        }
        return 0;
      },
    })),
  });

  const validationDataKey = JSON.stringify(
    validationQueries.map((q) => ({
      planId: q.data?.plan_id,
      isValid: q.data?.is_valid,
      errorCount: q.data?.error_count,
    })),
  );

  const validationData = useMemo(() => {
    return Object.fromEntries(
      (data?.media_plans ?? [])
        .map((plan, index) => {
          const query = validationQueries[index];
          return query?.data ? [plan.plan_id, query.data] : null;
        })
        .filter((entry): entry is [string, ValidationResponse] => entry !== null),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationDataKey, data?.media_plans]);

  // Get unique values for filters
  const allMediaPlans = useMemo(() => data?.media_plans || [], [data?.media_plans]);
  const isNonEmptyString = (v: string | null | undefined): v is string => Boolean(v);

  // Helper to get status for a plan
  const getStatus = (planId: string): 'Valid' | 'Errors' | 'Warnings' | 'unknown' => {
    const validation = validationData[planId];
    if (!validation) return 'unknown';
    if (validation.has_errors) return 'Errors';
    if (validation.has_warnings) return 'Warnings';
    return 'Valid';
  };

  // Multi-select filter matching: checks if a comma-separated filter value includes the field value
  const matchesPlanFilter = (
    filterVal: string,
    fieldVal: string | null | undefined,
    normalize?: (v: string) => string,
  ): boolean =>
    !filterVal ||
    (fieldVal != null &&
      filterVal
        .split(',')
        .some((v) => (normalize ? normalize(v) === normalize(fieldVal) : v === fieldVal)));

  // Dynamically compute available options for each filter based on the other active filters
  type PlanFilterKey = 'channel' | 'market' | 'business_unit' | 'status';
  const planFilterKeys: PlanFilterKey[] = ['channel', 'market', 'business_unit', 'status'];

  const availableOptions = useMemo(() => {
    const planMatchesFilter = (
      plan: MediaPlanSummary,
      key: PlanFilterKey,
      value: string,
    ): boolean => {
      if (!value) return true;
      const vals = value.split(',');
      if (key === 'channel') return plan.channel != null && vals.includes(plan.channel);
      if (key === 'market') return plan.market != null && vals.includes(plan.market);
      if (key === 'business_unit')
        return plan.business_unit != null && vals.includes(plan.business_unit);
      if (key === 'status') {
        const s = getStatus(plan.plan_id);
        return s === 'unknown' || vals.includes(s);
      }
      return true;
    };

    const computeOptions = (filterKey: PlanFilterKey): string[] => {
      const filtered = allMediaPlans.filter((plan) =>
        planFilterKeys.every(
          (key) => key === filterKey || planMatchesFilter(plan, key, filters[key]),
        ),
      );
      if (filterKey === 'status') {
        return [
          ...new Set(filtered.map((p) => getStatus(p.plan_id)).filter((s) => s !== 'unknown')),
        ].sort();
      }
      const rawValues = filtered
        .map((p) => p[filterKey as 'channel' | 'market' | 'business_unit'])
        .filter(isNonEmptyString);
      if (filterKey === 'channel') {
        return [...new Set(rawValues)].sort();
      }
      return [...new Set(rawValues)].sort();
    };

    return {
      channel: computeOptions('channel'),
      market: computeOptions('market'),
      business_unit: computeOptions('business_unit'),
      status: computeOptions('status'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMediaPlans, filters, validationData]);

  // Auto-clear filter selections that are no longer available
  // Handles comma-separated multi-select values by removing individual unavailable values
  useEffect(() => {
    if (isLoading) return;
    const newFilters = { ...filters };
    let changed = false;
    for (const key of planFilterKeys) {
      const value = filters[key];
      if (!value) continue;
      const parts = value.split(',');
      const validParts = parts.filter((v) => availableOptions[key].includes(v));
      const newValue = validParts.join(',');
      if (newValue !== value) {
        newFilters[key] = newValue;
        changed = true;
      }
    }
    if (changed) setFilters(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableOptions, isLoading]);

  // Apply filters
  const filteredMediaPlans = useMemo(() => {
    return allMediaPlans.filter((plan) => {
      if (!matchesPlanFilter(filters.channel, plan.channel)) return false;
      if (!matchesPlanFilter(filters.market, plan.market)) return false;
      if (!matchesPlanFilter(filters.business_unit, plan.business_unit)) return false;
      if (filters.status) {
        const status = getStatus(plan.plan_id);
        if (status === 'unknown') return true;
        if (!filters.status.split(',').includes(status)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMediaPlans, filters, validationData]);

  // Apply sorting
  const sortedMediaPlans = useMemo(() => {
    return [...filteredMediaPlans].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'plan_id':
          comparison = a.plan_id.localeCompare(b.plan_id);
          break;
        case 'campaign_count':
          comparison = a.campaign_count - b.campaign_count;
          break;
        case 'market':
          comparison = (a.market || '').localeCompare(b.market || '');
          break;
        case 'business_unit':
          comparison = (a.business_unit || '').localeCompare(b.business_unit || '');
          break;
        case 'year': {
          comparison =
            (a.year || '').localeCompare(b.year || '') ||
            (a.quarter || '').localeCompare(b.quarter || '');
          break;
        }
        case 'timeline': {
          const aDate = a.earliest_start_date ? new Date(a.earliest_start_date).getTime() : 0;
          const bDate = b.earliest_start_date ? new Date(b.earliest_start_date).getTime() : 0;
          comparison = aDate - bDate;
          break;
        }
        case 'status': {
          const statusOrder: Record<string, number> = {
            Errors: 0,
            Warnings: 1,
            Valid: 2,
            unknown: 3,
          };
          comparison =
            (statusOrder[getStatus(a.plan_id)] ?? 3) - (statusOrder[getStatus(b.plan_id)] ?? 3);
          break;
        }
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMediaPlans, sortField, sortDirection, validationData]);

  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length;
  const isFiltered = activeFilterCount > 0;

  // Handle deep link
  useEffect(() => {
    if (deepLinkPlanId && allMediaPlans.length > 0 && !hasProcessedDeepLink.current) {
      hasProcessedDeepLink.current = true;
      const targetPlan = allMediaPlans.find((p) => p.plan_id === deepLinkPlanId);
      if (targetPlan) {
        setHighlightedPlanId(targetPlan.plan_id);
        setTimeout(() => {
          const row = document.getElementById(`plan-row-${targetPlan.plan_id}`);
          if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
        setTimeout(() => setHighlightedPlanId(null), 5000);
      }
    }
  }, [deepLinkPlanId, allMediaPlans]);

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: approveMediaPlan,
    onSuccess: (response) => {
      toast.success(response.message);
      queryClient.invalidateQueries({ queryKey: ['pending-media-plans'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setApprovingPlanId(null);
    },
    onError: (error: unknown) => {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to approve media plan';
      toast.error(errorMessage);
      setApprovingPlanId(null);
    },
  });

  const toggleExpanded = (planId: string) => {
    setExpandedPlanId(expandedPlanId === planId ? null : planId);
  };

  const handleApprove = (plan: MediaPlanSummary) => {
    if (plan.has_dco && !dcoApprovalEnabled) {
      toast.error("Plans with DCO campaign lines can't be approved yet — coming soon.");
      return;
    }
    const validation = validationData[plan.plan_id];
    if (validation?.has_errors) {
      toast.error('Cannot approve media plan with validation errors. Please fix the issues first.');
      return;
    }
    setSelectedPlan(plan);
    setConfirmModalOpen(true);
  };

  const handleConfirmApproval = () => {
    if (selectedPlan) {
      setApprovingPlanId(selectedPlan.plan_id);
      approveMutation.mutate(selectedPlan.plan_id);
      setConfirmModalOpen(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const thClass =
    'px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors';

  // Error state
  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    return (
      <>
        <Breadcrumb />
        <div className="px-6">
          <div className="rounded-lg bg-red-50 border border-red-200 p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-red-900 mb-2">
                  Error Loading Media Plans
                </h2>
                <p className="text-red-700 mb-4">Failed to load pending media plans.</p>
                <details className="text-sm text-red-600 mb-4">
                  <summary className="cursor-pointer font-medium hover:text-red-800">
                    Technical Details
                  </summary>
                  <pre className="mt-2 p-3 bg-red-100 rounded border border-red-300 overflow-x-auto">
                    {errorMessage}
                  </pre>
                </details>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      <div ref={headerRef} className="shrink-0">
        <MediaPlanHeader
          totalPlans={allMediaPlans.length}
          filteredCount={filteredMediaPlans.length}
          isFiltered={isFiltered}
          filters={filters}
          onFilterChange={setFilters}
          uniqueChannels={availableOptions.channel}
          uniqueMarkets={availableOptions.market}
          uniqueBusinessUnits={availableOptions.business_unit}
          uniqueStatuses={availableOptions.status}
        />
      </div>
      <div className={`space-y-2 ${PAGE_PADDING}`}>
        {/* Media Plans Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {isLoading ? (
            <MediaPlanTableSkeleton headerHeight={headerHeight} />
          ) : sortedMediaPlans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">
                {isFiltered
                  ? 'No media plans match the selected filters'
                  : 'All media plans approved!'}
              </p>
            </div>
          ) : (
            <div
              className="overflow-auto"
              style={{
                maxHeight: tableScrollMaxHeight(headerHeight),
                scrollbarWidth: 'thin',
                scrollbarColor: '#D1D5DB #F3F4F6',
              }}
            >
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-[1]">
                  <tr>
                    <th className={thClass} onClick={() => handleSort('plan_id')}>
                      <div className="flex items-center gap-1">
                        Plan ID
                        <SortIcon
                          field="plan_id"
                          sortField={sortField}
                          sortDirection={sortDirection}
                        />
                      </div>
                    </th>
                    <th className={thClass} onClick={() => handleSort('campaign_count')}>
                      <div className="flex items-center gap-1">
                        Campaigns
                        <SortIcon
                          field="campaign_count"
                          sortField={sortField}
                          sortDirection={sortDirection}
                        />
                      </div>
                    </th>
                    <th className={thClass} onClick={() => handleSort('channel')}>
                      <div className="flex items-center gap-1">
                        Channel
                        <SortIcon
                          field="channel"
                          sortField={sortField}
                          sortDirection={sortDirection}
                        />
                      </div>
                    </th>
                    <th className={thClass} onClick={() => handleSort('market')}>
                      <div className="flex items-center gap-1">
                        Market
                        <SortIcon
                          field="market"
                          sortField={sortField}
                          sortDirection={sortDirection}
                        />
                      </div>
                    </th>
                    <th className={thClass} onClick={() => handleSort('business_unit')}>
                      <div className="flex items-center gap-1">
                        Business Unit
                        <SortIcon
                          field="business_unit"
                          sortField={sortField}
                          sortDirection={sortDirection}
                        />
                      </div>
                    </th>
                    <th className={thClass} onClick={() => handleSort('year')}>
                      <div className="flex items-center gap-1">
                        Year / Quarter
                        <SortIcon
                          field="year"
                          sortField={sortField}
                          sortDirection={sortDirection}
                        />
                      </div>
                    </th>
                    <th className={thClass} onClick={() => handleSort('timeline')}>
                      <div className="flex items-center gap-1">
                        Timeline
                        <SortIcon
                          field="timeline"
                          sortField={sortField}
                          sortDirection={sortDirection}
                        />
                      </div>
                    </th>
                    <th className={thClass} onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-1">
                        Status
                        <SortIcon
                          field="status"
                          sortField={sortField}
                          sortDirection={sortDirection}
                        />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {sortedMediaPlans.map((plan: MediaPlanSummary) => {
                    const planIndex = allMediaPlans.findIndex((p) => p.plan_id === plan.plan_id);
                    const validation = validationData[plan.plan_id];
                    const validationQuery = validationQueries[planIndex];
                    const isExpanded = expandedPlanId === plan.plan_id;
                    const isLoadingValidation = validationQuery?.isLoading ?? false;
                    const hasErrors = validation?.has_errors ?? false;
                    const hasWarnings = validation?.has_warnings ?? false;
                    const dcoBlocked = plan.has_dco && !dcoApprovalEnabled;

                    return (
                      <>
                        <tr
                          key={plan.plan_id}
                          id={`plan-row-${plan.plan_id}`}
                          className={`hover:bg-slate-50 transition-all duration-300 ${
                            highlightedPlanId === plan.plan_id
                              ? 'ring-2 ring-[#0014cc] ring-inset bg-blue-50'
                              : isExpanded
                                ? 'bg-slate-50'
                                : ''
                          }`}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-slate-900">{plan.plan_id}</div>
                              {plan.has_dco && (
                                <DcoBadge
                                  compact
                                  tooltip="This plan includes DCO (Dynamic Creative Optimization) campaign lines — they follow a different flow. Plans can mix DCO and non-DCO lines."
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg text-sm bg-blue-100 text-blue-800">
                              <span className="font-semibold">{plan.campaign_count}</span>
                              <span>campaigns</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {plan.channel ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg">
                                <ChannelIcon channel={plan.channel} />
                                {plan.channel}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {plan.market ? (
                              <span className="px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg inline-flex items-center gap-1.5">
                                <FlagIcon
                                  countryCode={plan.market}
                                  className="w-4 h-auto rounded-sm"
                                />
                                <span>{plan.market}</span>
                              </span>
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {plan.business_unit || '-'}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {plan.year || '-'} {plan.quarter ? `/ ${plan.quarter}` : ''}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {formatDate(plan.earliest_start_date)} -{' '}
                            {formatDate(plan.latest_end_date)}
                          </td>
                          <td className="px-4 py-4">
                            {isLoadingValidation ? (
                              <span className="inline-flex items-center gap-2 px-2 py-1 rounded-lg text-xs bg-slate-100 text-slate-600">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-slate-600"></div>
                                <span>Validating...</span>
                              </span>
                            ) : validation ? (
                              <button
                                onClick={() => toggleExpanded(plan.plan_id)}
                                className="cursor-pointer hover:opacity-80 transition-opacity"
                              >
                                {hasErrors ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-800">
                                    <XCircle className="w-3.5 h-3.5" />
                                    <span>
                                      {validation.error_count}{' '}
                                      {validation.error_count === 1 ? 'error' : 'errors'}
                                    </span>
                                    {isExpanded ? (
                                      <ChevronUp className="w-3 h-3 ml-0.5" />
                                    ) : (
                                      <ChevronDown className="w-3 h-3 ml-0.5" />
                                    )}
                                  </span>
                                ) : hasWarnings ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    <span>
                                      {validation.warning_count}{' '}
                                      {validation.warning_count === 1 ? 'warning' : 'warnings'}
                                    </span>
                                    {isExpanded ? (
                                      <ChevronUp className="w-3 h-3 ml-0.5" />
                                    ) : (
                                      <ChevronDown className="w-3 h-3 ml-0.5" />
                                    )}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    <span>Valid</span>
                                  </span>
                                )}
                              </button>
                            ) : (
                              <span className="text-sm text-slate-400">&mdash;</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {canApprove ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => handleApprove(plan)}
                                    disabled={
                                      approvingPlanId === plan.plan_id ||
                                      hasErrors ||
                                      dcoBlocked ||
                                      isLoadingValidation
                                    }
                                    className={`px-4 py-2 text-sm rounded-lg transition-colors disabled:cursor-not-allowed ${
                                      hasErrors || dcoBlocked
                                        ? 'bg-gray-300 text-gray-500'
                                        : 'bg-green-100 text-green-800 hover:bg-green-200 disabled:bg-gray-100 disabled:text-gray-400'
                                    }`}
                                  >
                                    {approvingPlanId === plan.plan_id ? (
                                      <span className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Approving...
                                      </span>
                                    ) : dcoBlocked ? (
                                      'Coming soon'
                                    ) : (
                                      'Approve'
                                    )}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-[240px]">
                                  {dcoBlocked && (
                                    <p className="font-medium mb-1">
                                      DCO plan approval is coming soon. Plans containing DCO
                                      campaign lines can't be approved yet.
                                    </p>
                                  )}
                                  {hasErrors && (
                                    <p className="font-medium mb-1">
                                      Fix validation errors before approving.
                                    </p>
                                  )}
                                  <p>
                                    Approving will make all campaigns visible in the Content Tracker
                                    and ready for production.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            ) : isLoadingPermissions ? (
                              <span className="text-sm text-slate-400">Loading...</span>
                            ) : isPermissionsError ? (
                              <span
                                className="text-sm text-red-400"
                                title="Could not load permissions"
                              >
                                Error
                              </span>
                            ) : (
                              <span
                                className="text-sm text-slate-400"
                                title="Only planners and admins can approve media plans"
                              >
                                View only
                              </span>
                            )}
                          </td>
                        </tr>
                        {/* Validation Details Row */}
                        {isExpanded && validation && (
                          <tr key={`${plan.plan_id}-details`}>
                            <td colSpan={9} className="p-0">
                              <div className="bg-slate-50 border-t border-slate-200 p-4">
                                {validation.errors.length > 0 && (
                                  <div className="mb-4">
                                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                                      <XCircle className="w-4 h-4" />
                                      Errors ({validation.error_count})
                                      <span className="text-xs font-normal text-red-600">
                                        — Must be fixed before approval
                                      </span>
                                    </h4>
                                    <GroupedIssues issues={validation.errors} variant="error" />
                                  </div>
                                )}
                                {validation.warnings.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                      <AlertTriangle className="w-4 h-4" />
                                      Warnings ({validation.warning_count})
                                      <span className="text-xs font-normal text-yellow-600">
                                        — Optional to fix
                                      </span>
                                    </h4>
                                    <GroupedIssues issues={validation.warnings} variant="warning" />
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModalOpen}
          onClose={() => {
            setConfirmModalOpen(false);
            setSelectedPlan(null);
          }}
          onConfirm={handleConfirmApproval}
          title="Approve Media Plan"
          message={
            selectedPlan ? (
              <div className="space-y-2 text-left">
                <p>
                  You are about to approve media plan <strong>{selectedPlan.plan_id}</strong>. This
                  will make {selectedPlan.campaign_count} campaign(s) visible in the Content Tracker
                  and ready for creative uploads.
                </p>
                {selectedPlan.has_dco && (
                  <p className="text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 text-sm">
                    This plan includes <strong>DCO campaign lines</strong>. DCO campaigns follow a
                    different flow after approval.
                  </p>
                )}
                <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                  <strong>Please make sure the plan data in MAP is final.</strong> If the plan is
                  updated in MAP after approval (e.g. nameplates, formats, or lines change), new
                  campaigns will be created — but the original campaigns with uploaded assets cannot
                  be automatically removed. This could lead to ghost or duplicated lines in the
                  tracker.
                </p>
              </div>
            ) : null
          }
          confirmText="Approve Plan"
          cancelText="Cancel"
          variant="warning"
          isLoading={approveMutation.isPending}
        />
      </div>
      <div className="mt-auto shrink-0">
        <MapSyncFooter />
      </div>
    </div>
  );
};
